import { useEffect, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

export function useVideoFramesV2({ videoSrc, frameRate }) {
  const [frames, setFrames] = useState([]);
  const [isReadyToPlay, setIsReadyToPlay] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  const ffmpegRef = useRef(null);
  const isCancelled = useRef(false);

  // Load video metadata to get duration
  useEffect(() => {
    if (!videoSrc) return;

    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = videoSrc;
    video.onloadedmetadata = () => {
      setVideoDuration(video.duration);
    };
  }, [videoSrc]);

  // Cleanup and cancel on unmount / video change
  useEffect(() => {
    isCancelled.current = false;
    return () => {
      isCancelled.current = true;

      cleanUpFrames();

      if (ffmpegRef.current) {
        console.log("Terminating FFmpeg…");
        ffmpegRef.current.terminate(); // stop FFmpeg worker
        ffmpegRef.current = null;
      }
    };
  }, [videoSrc]);

  // Initialize FFmpeg
  async function initializeFFmpeg() {
    if (!ffmpegRef.current && !isCancelled.current) {
      const ffmpeg = new FFmpeg();
      // FFmpeg crashes / internal errors
      ffmpeg.on("error", (err) => {
        console.error("❌ FFmpeg error event:", err);
      });

      ffmpeg.on("log", ({ message }) => {
        const timeMatch = message.match(/time=(\d+):(\d+):(\d+\.\d+)/);
        if (timeMatch) {
          const [, hh, mm, ss] = timeMatch;
          const timeInSeconds =
            parseInt(hh) * 3600 + parseInt(mm) * 60 + parseFloat(ss);

          if (videoDuration > 0) {
            const percent = Math.min(
              Math.round((timeInSeconds / videoDuration) * 100),
              100,
            );

            setExtractionProgress(percent);
          }
        }
      });

      await ffmpeg.load({ log: true });
      ffmpegRef.current = ffmpeg;
    }
    return ffmpegRef.current;
  }

  // Extract frames
  async function extractFrames() {
    if (!videoSrc) return;

    cleanUpFrames();
    setExtractionProgress(1);
    isCancelled.current = false;

    const ffmpeg = await initializeFFmpeg();
    if (!ffmpeg) return;

    try {
      // Write input video
      await ffmpeg.writeFile("video.mp4", await fetchFile(videoSrc));

      // Run extraction
      await ffmpeg.exec([
        "-i",
        "video.mp4",
        "-vf",
        `fps=${frameRate}`,
        "-q:v",
        "15",
        "frame%04d.jpg",
      ]);

      const files = await ffmpeg.listDir("/");
      const imageFiles = files.filter((file) => file.name.endsWith(".jpg"));
      const total = imageFiles.length;

      const imageUrls = [];
      for (let i = 0; i < total; i++) {
        if (isCancelled.current) break;

        const file = imageFiles[i];
        const imageData = await ffmpeg.readFile(file.name, "binary");
        if (isCancelled.current) break;

        const blob = new Blob([imageData], { type: "image/jpg" });
        const url = URL.createObjectURL(blob);

        imageUrls.push(url);
        setFrames([...imageUrls]);

        const percent = Math.round(((i + 1) / total) * 100);
        setExtractionProgress(percent);
      }

      // if (!isCancelled.current) setIsReadyToPlay(true);
    } catch (error) {
      console.error("Error extracting frames:", error);
    } finally {
      // setExtractionProgress(false);
      setIsReadyToPlay(true);
    }
  }

  // Cleanup frames
  function cleanUpFrames() {
    frames.forEach((url) => URL.revokeObjectURL(url));
    setFrames([]);
    setIsReadyToPlay(false);
    setExtractionProgress(0);
  }

  return {
    frames,
    extractFrames,
    cleanUpFrames,
    extractionProgress,
    isReadyToPlay,
  };
}
